import React, {useCallback, useEffect, useMemo} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {useStore} from 'effector-react';
import {Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {
    $editEmployeeModal,
    $employeePending,
    $selectedEmployeeForEdit,
    doUpdateEmployee,
    resendEmployeeInvitation,
    resetEmployeePassword,
    setEditEmployeeModal,
} from '@/entities/eployersControl/model';
import {Employee, getUserStatus, UserStatus} from '@/entities/eployersControl/type';
import {userModel} from '@/entities/user/model';
import {Button, ButtonVariant} from '@/shared/ui/Button';
import {Input} from '@/shared/ui/Input';
import {Modal} from '@/shared/ui/Modal';
import {Title} from '@/shared/ui/Title';

import {StatusLabel} from '../ui/StatusLabel';

import {EDIT_EMPLOYEE_FIELD_ROWS, EDIT_EMPLOYEE_FIELDS_I18N} from './fieldRows';
import {EditEmployeeFormValues, getEditEmployeeSchema} from './schema';
import {
    ActionsRow,
    Form,
    FormGrid,
    HeaderButtonRow,
    ModalHeaderBlock,
    Row,
    TitleRow,
} from './styled';

const emptyValues: EditEmployeeFormValues = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
};

function mapEmployeeToFormValues(e: Employee): EditEmployeeFormValues {
    return {
        firstName: e.firstName ?? '',
        lastName: e.lastName ?? '',
        email: e.email ?? '',
        phone: e.phone ?? '',
    };
}

export const EditEmployeeModal = () => {
    const show = useStore($editEmployeeModal);
    const employee = useStore($selectedEmployeeForEdit);
    const employePending = useStore($employeePending);
    const {t, i18n} = useTranslation();
    const isOperator = useStore(userModel.isOperator);
    const isChiefInspector = useStore(userModel.isChiefInspector);
    const isDirector = useStore(userModel.isDirector);

    const title = useMemo(() => {
        if (isDirector) return 'EMPLOYEE';
        if (isOperator) return 'CHIEF_INSPECTOR';
        if (isChiefInspector) return 'INSPECTOR';
        return 'EMPLOYEE';
    }, [isDirector, isChiefInspector, isOperator, t]);

    const translateValidates = useCallback(
        (key: string, replace?: Record<string, unknown>) =>
            t(`page_employers_control.edit_employee.validations.${key}`, replace),
        [i18n.language, t],
    );

    const schema = useMemo(() => getEditEmployeeSchema(translateValidates), [translateValidates]);

    const {control, handleSubmit, formState, errors, reset} = useForm<EditEmployeeFormValues>({
        resolver: yupResolver(schema),
        mode: 'all',
        defaultValues: emptyValues,
    });

    useEffect(() => {
        if (!show) {
            reset(emptyValues);
            return;
        }
        if (employee) {
            reset(mapEmployeeToFormValues(employee));
        }
    }, [show, employee, reset]);

    const userStatus = useMemo<UserStatus>(
        () => (employee ? getUserStatus(employee) : 'INACTIVE'),
        [employee],
    );

    const onClose = useCallback(() => {
        setEditEmployeeModal(false);
    }, []);

    const onSubmit = useCallback(
        (data: EditEmployeeFormValues) => {
            if (!employee) return;

            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const {isActive, taxAuthorityDepartment, ...rest} = employee;
            const bodyToUpdate = {
                ...rest,
                ...data,
                active: isActive,
            };

            doUpdateEmployee({
                userId: employee.id,
                body: bodyToUpdate,
            });
        },
        [employee],
    );

    const onEnableAccess = useCallback(
        ({userActive}: {userActive: boolean}) => {
            if (employee) {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const {isActive, taxAuthorityDepartment, ...rest} = employee;
                const bodyToUpdate = {
                    ...rest,
                    active: userActive,
                };

                doUpdateEmployee({
                    userId: employee.id,
                    body: bodyToUpdate,
                });
            }
        },
        [employee],
    );

    const onResendInvitation = useCallback(() => {
        if (employee)
            resendEmployeeInvitation({
                firstName: employee.firstName,
                userName: employee.email,
                email: employee.email,
            });
    }, [employee]);

    const onResetPassword = useCallback(() => {
        if (employee) {
            resetEmployeePassword({
                firstName: employee.firstName,
                userName: employee.email,
                email: employee.email,
            });
        }
    }, [employee]);

    if (!show || !employee) {
        return null;
    }

    return (
        <Modal modalWidth={2} show={show} onClose={onClose}>
            <ModalHeaderBlock>
                <TitleRow>
                    <Title size={4}>{t(`roles.${employee.role ?? title}`)}</Title>
                    <StatusLabel status={userStatus}>
                        {t(`page_employers_control.edit_employee.status.${userStatus}`)}
                    </StatusLabel>
                </TitleRow>
                <HeaderButtonRow>
                    <Button
                        isLoading={employePending}
                        size="sm"
                        type="button"
                        variant={ButtonVariant.Text}
                        onClick={() => onEnableAccess({userActive: userStatus === 'INACTIVE'})}
                    >
                        {userStatus === 'INACTIVE'
                            ? t('page_employers_control.control.enableAccess')
                            : t('page_employers_control.control.disableAccess')}
                    </Button>
                </HeaderButtonRow>
            </ModalHeaderBlock>
            <Form onSubmit={handleSubmit(onSubmit)} autoComplete="off" noValidate>
                <FormGrid>
                    {EDIT_EMPLOYEE_FIELD_ROWS.map((row, rowIndex) => (
                        <Row key={rowIndex}>
                            {row.map((field) => {
                                const fieldError = errors[field.name];
                                const isDirty = formState.dirtyFields[field.name];

                                return (
                                    <Controller
                                        key={field.name}
                                        name={field.name}
                                        control={control}
                                        render={({onChange, value, name}) => {
                                            const hasValue = String(value ?? '').trim().length > 0;

                                            return (
                                                <Input
                                                    disabled={
                                                        field.name === 'email' || employePending
                                                    }
                                                    label={t(
                                                        `${EDIT_EMPLOYEE_FIELDS_I18N}.${field.labelKey}`,
                                                    )}
                                                    name={name}
                                                    formFieldName={name}
                                                    type={field.type}
                                                    value={value}
                                                    onChange={onChange}
                                                    hasError={!!fieldError}
                                                    isValid={!!isDirty && !fieldError && hasValue}
                                                />
                                            );
                                        }}
                                    />
                                );
                            })}
                        </Row>
                    ))}
                </FormGrid>
                <ActionsRow>
                    <div>
                        <Button
                            isLoading={employePending}
                            style={{whiteSpace: 'nowrap'}}
                            type="submit"
                            disabled={!formState.isDirty || !formState.isValid}
                        >
                            {t('page_employers_control.edit_employee.save')}
                        </Button>
                    </div>
                    <div>
                        <Button
                            isLoading={employePending}
                            style={{whiteSpace: 'nowrap'}}
                            type="button"
                            variant={ButtonVariant.Secondary}
                            onClick={onResendInvitation}
                        >
                            {t('page_employers_control.control.resendInvitation')}
                        </Button>
                    </div>
                    <div>
                        <Button
                            isLoading={employePending}
                            style={{whiteSpace: 'nowrap'}}
                            type="button"
                            variant={ButtonVariant.Outline}
                            onClick={onResetPassword}
                        >
                            {t('page_employers_control.control.resetPassword')}
                        </Button>
                    </div>
                </ActionsRow>
            </Form>
        </Modal>
    );
};
